/**
 * useSearchV2 — prosty 3-stanowy hook wyszukiwania RAG.
 * Stany: idle → searching (crawling, lista standardowa) → results (lista wyników, tło dim)
 * Dismiss: results → idle (tap mika)
 */

import { useCallback, useRef, useState } from 'react';
import { getNormalizedResults } from '../services/SearchBridge';
import type { NormalizedSearchResult } from '../services/SearchBridge';
import { getOfflineNormalizedResults } from '../services/offlineSearchResults';

export type SearchPhase = 'idle' | 'searching' | 'results';

export interface SearchUIState {
  phase: SearchPhase;
  /** Czy pokazywać animację pełzającą */
  showCrawling: boolean;
  /** Czy tło przyciemnione (faza results) */
  isDimmed: boolean;
  /** Wyniki wyszukiwania — null gdy brak / w trakcie */
  searchResult: NormalizedSearchResult | null;
  /** Komunikat błędu */
  errorMessage: string | null;
  /** Ostatnie zapytanie */
  query: string | null;
}

export interface UseSearchV2Return {
  state: SearchUIState;
  startSearch: (query: string) => void;
  dismissSearch: () => void;
  forceResetSearch: () => void;
}

const IDLE: SearchUIState = {
  phase: 'idle',
  showCrawling: false,
  isDimmed: false,
  searchResult: null,
  errorMessage: null,
  query: null,
};

export function useSearchV2(): UseSearchV2Return {
  const [state, setState] = useState<SearchUIState>(IDLE);
  const abortRef = useRef<boolean>(false);

  const startSearch = useCallback(async (query: string) => {
    // Anuluj poprzednie wyszukiwanie
    abortRef.current = true;
    const myAbort = { cancelled: false };
    abortRef.current = false;

    setState({ ...IDLE, phase: 'searching', showCrawling: true, query });

    let searchResult: NormalizedSearchResult | null = null;
    let errorMessage: string | null = null;

    // Offline: NIE strzelamy do /api/screen1/search-rag — filtrujemy notatki
    // zgromadzone lokalnie (services/offlineSearchResults.ts). Online: standardowy RAG.
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;

    try {
      if (online) {
        try {
          searchResult = await getNormalizedResults(query);
        } catch {
          // Edge Search Fallback: navigator.onLine skłamał (serwer padł, brak
          // realnej łączności mimo aktywnego interfejsu) — zamiast pokazywać
          // błąd, przeszukaj notatki zgromadzone lokalnie. Gdy sieć naprawdę
          // wróci, kolejne wywołanie startSearch znów przeczyta online=true.
          searchResult = await getOfflineNormalizedResults(query);
        }
      } else {
        searchResult = await getOfflineNormalizedResults(query);
      }
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : String(err);
    }

    if (myAbort.cancelled) return;

    setState({
      phase: 'results',
      showCrawling: false,
      isDimmed: true,
      searchResult,
      errorMessage,
      query,
    });
  }, []);

  const dismissSearch = useCallback(() => {
    abortRef.current = true;
    setState(IDLE);
  }, []);

  const forceResetSearch = useCallback(() => {
    abortRef.current = true;
    setState(IDLE);
  }, []);

  return { state, startSearch, dismissSearch, forceResetSearch };
}
